import { useEffect, useRef } from 'react';
import type { LegacyRef } from 'react';
import clsx from 'clsx';

import {
  Box,
  Overlay,
  ScrollableCard,
  Scroller,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Tooltip,
  Transition,
  withErrorBoundary,
} from '@highlight-cp/core-ui';
import { useIsSticky, useRemainingHeight } from '@highlight-cp/hooks';

import classes from './right-sidebar-card.module.css';

export interface RightSidebarCardTab {
  disabled?: boolean;
  disabledTooltip?: string;
  hidden?: boolean;
  label: string | undefined;
  value: string;
  content: React.ReactNode;
}

interface RightSidebarCardProps {
  activeTab?: string;
  className?: string;
  glowPulseEffect?: boolean;
  isReadOnly?: boolean;
  hideTabsMenu?: boolean;
  onTabChange?: (value: string) => void;
  tabs?: RightSidebarCardTab[];
  children?: React.ReactNode;
}

const RightSidebarCard = ({
  activeTab,
  children,
  className,
  glowPulseEffect,
  hideTabsMenu,
  isReadOnly = false,
  onTabChange,
  tabs,
}: RightSidebarCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { ref: sidebarRef, height: maxSidebarHeight } = useRemainingHeight({ bottomPadding: 16 });
  const { isSticky } = useIsSticky(cardRef);

  useEffect(() => {
    // scroll to top when changing tab or activating from button(pulsing)
    if (sidebarRef?.current?.scrollTo) {
      sidebarRef.current.scrollTo(0, 0);
    }
  }, [sidebarRef, activeTab, glowPulseEffect]);

  return (
    <aside ref={cardRef} className={clsx(classes.root, isSticky && classes.sticky)}>
      <Transition mounted={isReadOnly} transition="fade">
        {/* v7: `opacity` became an element-level style prop; the background
            alpha (v6 `opacity`) is now `backgroundOpacity` */}
        {(transitionStyle) => (
          <Overlay
            color="#FFF"
            backgroundOpacity={0.4}
            style={transitionStyle}
            className={classes.overlay}
          />
        )}
      </Transition>
      <ScrollableCard
        ref={sidebarRef as LegacyRef<HTMLDivElement>}
        radius="md"
        className={`${className} ${classes.card} ${glowPulseEffect ? classes.glowPulseEffect : ''}`}
        mah={maxSidebarHeight ? `${maxSidebarHeight}px` : undefined}
      >
        {tabs?.length ? (
          <Tabs
            value={activeTab}
            variant="pills"
            onChange={(value) => value !== null && onTabChange?.(value)}
            keepMounted={false}
          >
            {!hideTabsMenu && (
              <div className={classes.tabsListWrapper}>
                <TabsList>
                  <Scroller endControlProps={{ tabIndex: -1 }} startControlProps={{ tabIndex: -1 }}>
                    {tabs
                      .filter((tab) => !tab.hidden)
                      .map((tab) => {
                        const isDisabled = tab.disabled && !isReadOnly;
                        const tabElement = (
                          <TabsTab
                            key={tab.value}
                            value={tab.value}
                            disabled={isDisabled}
                            px="sm"
                            // 6px, not the xs token's 10px — keeps the compact row height.
                            py={6}
                          >
                            {tab.label ?? 'tab'}
                          </TabsTab>
                        );

                        if (isDisabled && tab.disabledTooltip) {
                          return (
                            <Tooltip key={tab.value} label={tab.disabledTooltip}>
                              <span className={classes.disabledTabWrapper}>{tabElement}</span>
                            </Tooltip>
                          );
                        }

                        return tabElement;
                      })}
                  </Scroller>
                </TabsList>
              </div>
            )}
            <div className={classes.tabsPanelWrapper}>
              {tabs.map((tab) => (
                <TabsPanel key={tab.value} value={tab.value}>
                  {tab.content}
                </TabsPanel>
              ))}
            </div>
          </Tabs>
        ) : (
          <Box py="lg" px="md">
            {children}
          </Box>
        )}
      </ScrollableCard>
    </aside>
  );
};

export default withErrorBoundary(RightSidebarCard, {
  componentName: 'right sidebar card',
});
